import { useCallback, useEffect, useRef, useState } from 'react'
import { Card } from '@heroui/react'
import { api } from '../lib/api'
import { saysNoBranch } from '../lib/adminBranches'
import { rememberBranch, rememberedBranch, startingBranch } from '../lib/branchChoice'
import { mayRead, SECTION_READS } from '../lib/adminReads'
import { MARKS_AND_OUTCOMES } from '../lib/features'
import { explain } from '../lib/staffErrors'
import { endPass, hasUnsaved, passUnsaved } from '../lib/unsaved'
import { t, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { SetupAtNote, Topbar } from '../ui/Chrome'
import type { IconName } from '../ui/icons'
import { OrganisationNeeded } from '../ui/OrgSwitch'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { BookingBranches } from '../admin/BookingBranches'
import { BookingKeys } from '../admin/BookingKeys'
import { BookingRules } from '../admin/BookingRules'
import { Branches } from '../admin/Branches'
import { Connectors } from '../admin/Connectors'
import { DataFeed } from '../admin/DataFeed'
import { Deliveries } from '../admin/Deliveries'
import { Directory } from '../admin/Directory'
import { EquipmentSection } from '../admin/equipment/EquipmentSection'
import { Fields } from '../admin/Fields'
import { Options } from '../admin/Options'
import { Languages } from '../admin/Languages'
import { Licence } from '../admin/Licence'
import { Mail } from '../admin/Mail'
import { Marks } from '../admin/Marks'
import { Network } from '../admin/Network'
import { Notifications } from '../admin/Notifications'
import { Organisations } from '../admin/Organisations'
import { Outcomes } from '../admin/Outcomes'
import { OperationProfiles } from '../admin/profiles/OperationProfiles'
import { SettingsMenu, type MenuEntry, type MenuGroup } from '../admin/SettingsMenu'
import { DiscardAsk, NEW, Notice, PageHead, SubPageProvider, type SubPageNav } from '../admin/settingsKit'
import { QueueAlerts } from '../admin/QueueAlerts'
import { Roles } from '../admin/Roles'
import { ScreenApps } from '../admin/ScreenApps'
import { Services } from '../admin/Services'
import { Settings } from '../admin/Settings'
import { SingleSignOn } from '../admin/SingleSignOn'
import { Webhooks } from '../admin/Webhooks'
import { Wording } from '../admin/Wording'
import type { Branch, Identity } from '../lib/types'
import '../styles/admin.css'
import '../styles/settings.css'

/**
 * Settings: a left menu of every section this person may open, in groups, and the
 * open section beside it. Sections are offered from what the server said this
 * person may do, never from their role. The branch is chosen in the top bar.
 */

type SectionKey =
  | 'options'
  | 'languages'
  | 'wording'
  | 'services'
  | 'opProfiles'
  | 'roles'
  | 'directory'
  | 'sso'
  | 'branches'
  | 'network'
  | 'bookingBranches'
  | 'bookingRules'
  | 'equipment'
  | 'screenApps'
  | 'mail'
  | 'notifications'
  | 'webhooks'
  | 'dataFeed'
  | 'deliveries'
  | 'connectors'
  | 'bookingKeys'
  | 'fields'
  | 'outcomes'
  | 'marks'
  | 'alerts'
  | 'organisations'
  | 'licence'
  | 'settings'

/** What a change reaches: one branch, the organisation, outside systems, the server. */
type GroupKey = 'branch' | 'org' | 'connections' | 'install' | 'booking'

/** The menu's groups. */
export type CategoryKey =
  | 'organisation'
  | 'people'
  | 'branches'
  | 'queues'
  | 'booking'
  | 'screens'
  | 'communication'
  | 'connections'
  | 'installation'

interface Section {
  key: SectionKey
  label: StaffKey
  /** Words a person might type looking for this section, beside its name. */
  also: StaffKey
  icon: IconName
  /** The permission it needs, or a list of which any one will do. */
  permission: string | readonly string[]
  group: GroupKey
  category: CategoryKey
  /** A sub-heading in the group, over a run of sections. */
  part?: StaffKey
  /** A licence module this section needs, beyond the permission. */
  module?: string
  /** Stays offered through a licence lock while identity.boughtModules has the module:
   *  it holds a switch-off, delete or close-test-page control a lapse still needs. */
  reachableWhenLocked?: boolean
  /** Refused by the server to anyone limited to named branches (WHOLE_ORGANISATION_ONLY). */
  wholeOrganisation?: boolean
}

/** The groups in the menu's order. */
export const CATEGORIES: ReadonlyArray<{ key: CategoryKey; label: StaffKey }> = [
  { key: 'organisation', label: 'settings.group.organisation' },
  { key: 'people', label: 'settings.group.people' },
  { key: 'branches', label: 'settings.group.branches' },
  { key: 'queues', label: 'settings.group.queues' },
  { key: 'booking', label: 'settings.group.booking' },
  { key: 'screens', label: 'settings.group.screens' },
  { key: 'communication', label: 'settings.group.communication' },
  { key: 'connections', label: 'settings.group.connections' },
  { key: 'installation', label: 'settings.group.installation' },
]

const QUEUE_READ = ['profiles.manage', 'catalogue.manage', 'branch.manage'] as const

// The order here is the menu's order inside each group.
const SECTIONS: Section[] = [
  { key: 'options', label: 'settings.general', also: 'org.opt.also', icon: 'organisations', permission: ['catalogue.manage', 'branch.manage'], group: 'org', category: 'organisation' },
  { key: 'languages', label: 'settings.languages', also: 'settings.languages.also', icon: 'globe', permission: ['catalogue.manage', 'branch.manage'], group: 'org', category: 'organisation' },
  // The whole organisation's words, so refused (like Options) to anyone confined to named branches.
  { key: 'wording', label: 'wording.section', also: 'wording.also', icon: 'edit', permission: ['catalogue.manage', 'branch.manage'], group: 'org', category: 'organisation', wholeOrganisation: true },
  { key: 'fields', label: 'admin.section.fields', also: 'admin.also.fields', icon: 'table', permission: 'catalogue.manage', group: 'org', category: 'organisation' },
  { key: 'outcomes', label: 'admin.section.outcomes', also: 'admin.also.outcomes', icon: 'check', permission: 'catalogue.manage', group: 'org', category: 'organisation' },
  { key: 'marks', label: 'admin.section.marks', also: 'admin.also.marks', icon: 'tag', permission: 'catalogue.manage', group: 'org', category: 'organisation' },

  { key: 'roles', label: 'admin.section.roles', also: 'admin.also.roles', icon: 'lock', permission: 'staff.manage', group: 'org', category: 'people' },
  { key: 'directory', label: 'admin.section.directory', also: 'admin.also.directory', icon: 'shield', permission: 'staff.manage', group: 'connections', category: 'people' },
  // Read like the directory; set by the installation owner alone (internal/api/sso.go).
  { key: 'sso', label: 'admin.section.sso', also: 'admin.also.sso', icon: 'key', permission: 'staff.manage', group: 'connections', category: 'people' },

  // Branch groups is branch.manage, as the server asks on every route (reachBranchSetup).
  { key: 'branches', label: 'admin.section.branchManagement', also: 'admin.also.branches', icon: 'pin', permission: 'branch.manage', group: 'org', category: 'branches' },
  { key: 'network', label: 'admin.section.network', also: 'admin.also.network', icon: 'layers', permission: 'branch.manage', group: 'org', category: 'branches' },

  { key: 'services', label: 'admin.section.services', also: 'admin.also.services', icon: 'grid', permission: 'catalogue.manage', group: 'org', category: 'queues' },
  // Read by the catalogue and branch managers too (R8), who see it read-only.
  { key: 'opProfiles', label: 'profiles.section', also: 'profiles.also', icon: 'filter', permission: QUEUE_READ, group: 'org', category: 'queues' },
  // Written by profiles.manage like the profile editor.
  { key: 'equipment', label: 'admin.section.equipment', also: 'admin.also.equipment', icon: 'machine', permission: QUEUE_READ, group: 'org', category: 'queues' },
  // Queue alerts are core, not a sold module, so no licence gates them.
  { key: 'alerts', label: 'admin.section.alerts', also: 'admin.also.alerts', icon: 'bell', permission: 'catalogue.manage', group: 'branch', category: 'queues' },

  { key: 'bookingBranches', label: 'booking.section.branches', also: 'booking.also.branches', icon: 'booking', permission: 'appointment.manage', group: 'booking', category: 'booking', module: 'appointments' },
  // Rules carries the test-page switch a lapse leaves open.
  { key: 'bookingRules', label: 'booking.section.rules', also: 'booking.also.rules', icon: 'clock', permission: 'booking.manage', group: 'booking', category: 'booking', module: 'appointments', reachableWhenLocked: true, wholeOrganisation: true },
  // Keys carries the switch-off and delete a leaked key needs during a lapse.
  { key: 'bookingKeys', label: 'booking.section.keys', also: 'booking.also.keys', icon: 'key', permission: 'booking.manage', group: 'booking', category: 'booking', module: 'appointments', reachableWhenLocked: true, wholeOrganisation: true },

  // Whoever places an app on a machine sees the apps; writing stays tenant.manage (P10b, ruling 9).
  { key: 'screenApps', label: 'admin.section.screenApps', also: 'admin.also.screenApps', icon: 'floor', permission: 'catalogue.manage', group: 'connections', category: 'screens', module: 'screenApps' },

  // Mail, webhooks and connectors are the installation owner's (tenant.manage).
  { key: 'mail', label: 'admin.section.mail', also: 'admin.also.mail', icon: 'mail', permission: 'tenant.manage', group: 'connections', category: 'communication' },
  { key: 'notifications', label: 'admin.nav.notifications', also: 'admin.also.notifications', icon: 'message', permission: 'catalogue.manage', group: 'org', category: 'communication' },
  { key: 'deliveries', label: 'admin.section.deliveries', also: 'admin.also.deliveries', icon: 'audit', permission: 'catalogue.manage', group: 'connections', category: 'communication' },

  { key: 'webhooks', label: 'admin.section.webhooks', also: 'admin.also.webhooks', icon: 'link', permission: 'tenant.manage', group: 'connections', category: 'connections' },
  { key: 'dataFeed', label: 'admin.section.dataFeed', also: 'admin.also.dataFeed', icon: 'download', permission: 'staff.manage', group: 'connections', category: 'connections', wholeOrganisation: true },
  { key: 'connectors', label: 'admin.section.connectors', also: 'admin.also.connectors', icon: 'plug', permission: 'tenant.manage', group: 'connections', category: 'connections', module: 'screenApps' },

  { key: 'organisations', label: 'admin.section.organisations', also: 'admin.also.organisations', icon: 'globe', permission: 'tenant.manage', group: 'install', category: 'installation' },
  { key: 'licence', label: 'admin.section.licence', also: 'admin.also.licence', icon: 'document', permission: 'licence.read', group: 'install', category: 'installation' },
  { key: 'settings', label: 'admin.section.settings', also: 'admin.also.settings', icon: 'admin', permission: 'settings.manage', group: 'install', category: 'installation' },
]

/** The group a section sits in: anyone but the installation owner reads the licence under Organisation. */
export function categoryOf(section: Pick<Section, 'key' | 'category'>, owner: boolean | undefined): CategoryKey {
  if (section.key === 'licence' && !owner) return 'organisation'
  return section.category
}

/** Staff pages of their own that a group points at. */
export interface OutsidePage {
  key: string
  label: StaffKey
  also: StaffKey
  icon: IconName
  to: string
  category: CategoryKey
}

export function outsidePages(identity: Pick<Identity, 'permissions' | 'modules'>): OutsidePage[] {
  const can = (p: string) => (identity.permissions as readonly string[]).includes(p)
  const all: Array<OutsidePage & { show: boolean }> = [
    { key: 'people', label: 'entry.app.people', also: 'admin.also.people', icon: 'people', to: '/people', category: 'people', show: can('staff.manage') },
  ]
  return all.filter((o) => o.show).map(({ show: _show, ...o }) => o)
}

/** Sections not yet in the new frame: the shell draws their head over their old white
 *  panel. A converted section draws its own PageHead and cards; delete its line here.
 *  One key per line, in the conversion groups of docs/design/settings-redesign.md. */
export const LEGACY_FRAME: ReadonlySet<SectionKey> = new Set<SectionKey>([
])

/** Sections whose reads name one organisation: the server refuses them (422) for an owner who has chosen none.
 *  Branches is absent: with none chosen it lists every organisation's branches. */
const NEEDS_ORGANISATION: ReadonlySet<SectionKey> = new Set<SectionKey>([
  'options', 'languages', 'wording', 'opProfiles', 'services', 'outcomes', 'marks', 'fields', 'notifications', 'network', 'roles',
  'directory', 'sso', 'mail', 'webhooks', 'dataFeed', 'deliveries', 'screenApps', 'connectors', 'equipment',
  'bookingBranches', 'bookingRules', 'bookingKeys',
])

/** The title says what a section is; these few need one more line. */
const HINTS: Partial<Record<SectionKey, StaffKey>> = {
  marks: 'admin.hint.marks',
  equipment: 'admin.hint.equipment',
}

/** The address after #: a section, then its steps (a branch's code, a record's id, deeper steps). */
/** Old addresses of sections that became another (5 Nov 2026): Basic's, and Approvals, now on the branch's page. */
const MOVED: Record<string, string> = { basicQueues: 'opProfiles', callingProfiles: 'opProfiles', approvals: 'branches' }

export function parseHash(hash: string): { key: string; parts: string[] } {
  const [named = '', ...rest] = hash.replace(/^#/, '').split('/')
  const key = MOVED[named] ?? named
  const parts = rest.filter(Boolean).map((p) => {
    try {
      return decodeURIComponent(p)
    } catch {
      // A mangled step is looked for as written, and found by nothing.
      return p
    }
  })
  return { key, parts }
}

const readHash = () => parseHash(window.location.hash)

/** The address of a section and its steps. */
export function hashOf(key: string, parts: readonly string[]): string {
  return '#' + [key, ...parts.map(encodeURIComponent)].join('/')
}

/** Sections whose first step is a branch's code (a link from a branch's page); a record's page comes after it. */
export const BRANCH_CODED: ReadonlySet<string> = new Set(['alerts', 'bookingBranches'])

/** The section an address names, or '' when this person cannot open it (then the
 *  first section opens): never the section they were on before. */
export function sectionForHash(key: string, availableKeys: readonly string[]): SectionKey | '' {
  return key !== '' && availableKeys.includes(key) ? (key as SectionKey) : ''
}

/** English headings only for the installation owner on an installation page. */
export function headingLang(group: GroupKey, owner: boolean | undefined, lang: StaffLanguage): StaffLanguage {
  return group === 'install' && owner ? 'en' : lang
}

/** The sections a person may open: what their permissions allow and, for a module's
 *  section, only when /me lists the module — or, for those marked reachableWhenLocked,
 *  when boughtModules says it was bought though a lock has emptied modules. */
export function availableSections(
  permissions: readonly string[],
  modules: readonly string[] | undefined,
  boughtModules: readonly string[] | undefined = [],
): Section[] {
  return SECTIONS.filter((s) => {
    if (!MARKS_AND_OUTCOMES && (s.key === 'outcomes' || s.key === 'marks')) return false
    const needs = typeof s.permission === 'string' ? [s.permission] : s.permission
    if (!needs.some((p) => permissions.includes(p))) return false
    if (!s.module) return true
    if ((modules ?? []).includes(s.module)) return true
    return Boolean(s.reachableWhenLocked) && (boughtModules ?? []).includes(s.module)
  })
}

/** What this person may open here, as the page itself offers it. */
export function sectionsFor(identity: Pick<Identity, 'permissions' | 'modules' | 'boughtModules' | 'staff'>): Section[] {
  const pinned = (identity.staff.branchIds ?? []).length > 0 && !identity.staff.owner
  const reads = (key: SectionKey) => (SECTION_READS[key]?.needs ?? []).every((r) => mayRead(identity.permissions, r))
  return availableSections(identity.permissions, identity.modules, identity.boughtModules).filter(
    (s) => !(pinned && s.wholeOrganisation) && reads(s.key),
  )
}

/** One menu entry: a section here, or a page of its own. */
type Entry =
  | { kind: 'section'; key: SectionKey; label: StaffKey; also: StaffKey; icon: IconName; part?: StaffKey }
  | ({ kind: 'page' } & OutsidePage)

/** The menu's groups for this person, each with its entries in order; empty groups dropped. */
export function menuOf(sections: readonly Section[], outside: readonly OutsidePage[], owner: boolean | undefined): Array<{ key: CategoryKey; label: StaffKey; entries: Entry[] }> {
  return CATEGORIES.map((c) => ({
    ...c,
    entries: [
      ...outside.filter((o) => o.category === c.key).map((o): Entry => ({ kind: 'page', ...o })),
      ...sections
        .filter((s) => categoryOf(s, owner) === c.key)
        .map((s): Entry => ({ kind: 'section', key: s.key, label: s.label, also: s.also, icon: s.icon, part: s.part })),
    ],
  })).filter((c) => c.entries.length > 0)
}

export function Administration({
  identity,
  onNavigate,
}: {
  identity: Identity
  onNavigate?: (to: string) => void
}) {
  const { lang, say } = useStaffText()
  const can = (permission: string) => identity.permissions.includes(permission as never)
  const owner = identity.staff.owner
  const available = sectionsFor(identity)
  const outside = outsidePages(identity)
  // Bought, by the document, but missing from modules because the licence is locked
  // now: Rules and Keys then leave only their switch-off, delete and close controls live.
  const bookingLocked =
    Boolean(identity.boughtModules?.includes('appointments')) && !(identity.modules ?? []).includes('appointments')

  // #alerts opens a section directly; none opens the first one.
  const [section, setSection] = useState<SectionKey | ''>(() => sectionForHash(readHash().key, available.map((s) => s.key)))
  const [branches, setBranches] = useState<Branch[]>([])
  // Whether the branch list has answered: until then "No branch yet" would be a guess.
  const [branchesRead, setBranchesRead] = useState(false)
  const [branchId, setBranchId] = useState('')
  // One organisation for the whole app, chosen in the top bar (ui/OrgSwitch).
  const orgContext = useSession()?.org
  const organisation = orgContext?.organisation ?? ''
  // The steps after the section: a branch's code, a profile's id, an open record.
  const [hashParts, setHashParts] = useState<string[]>(() => (section ? readHash().parts : []))
  const hashCode = hashParts[0] ?? ''
  // A record's page asked to go Back; a second close before the address answers is the same one.
  const closing = useRef(false)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('')
  // A move away from unsaved edits, waiting for Keep editing or Discard.
  const [leaving, setLeaving] = useState<(() => void) | null>(null)

  const guarded = (go: () => void) => {
    if (hasUnsaved()) setLeaving(() => go)
    else go()
  }

  const loadBranches = useCallback(async () => {
    try {
      const listed = await api.branches()
      setBranches(listed.branches)
      setBranchesRead(true)
      const ours = organisation ? listed.branches.filter((b) => b.tenantId === organisation) : listed.branches
      setBranchId((current) => startingBranch(ours, rememberedBranch(), current))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    }
  }, [lang, organisation])

  const mayCrossOrganisations = identity.permissions.includes('tenant.manage')
  // The server's answer; an older server sends none, and then it is the owner's alone.
  const setsUpSignIn = identity.setsUpSignIn ?? mayCrossOrganisations

  useEffect(() => {
    void loadBranches()
  }, [loadBranches])

  // A link to another section of this page (#alerts) is followed without a reload.
  const availableKeys = available.map((s) => s.key).join(',')
  useEffect(() => {
    const on = (e: HashChangeEvent) => {
      // A page closing itself (Done, Save) asks nothing: its edits went into the page below.
      if (!closing.current && e.oldURL && e.newURL && hasUnsaved(new URL(e.newURL).hash)) {
        const to = e.newURL
        history.replaceState(history.state, '', e.oldURL)
        setLeaving(() => () => {
          passUnsaved()
          window.location.href = to
        })
        return
      }
      endPass()
      closing.current = false
      const { key, parts } = readHash()
      const next = sectionForHash(key, availableKeys.split(','))
      setSection(next)
      setHashParts(next ? parts : [])
      if (!next && key) history.replaceState(history.state, '', window.location.pathname)
    }
    addEventListener('hashchange', on)
    return () => removeEventListener('hashchange', on)
  }, [availableKeys])

  // An address naming a section this person cannot open drops it, and the first section opens.
  useEffect(() => {
    if (!section && readHash().key) history.replaceState(history.state, '', window.location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A branch named in the address becomes the branch in the top bar.
  useEffect(() => {
    if (!hashCode || (section !== 'branches' && !BRANCH_CODED.has(section))) return
    const named = branches.find((b) => b.code === hashCode)
    if (named) {
      setBranchId(named.id)
      rememberBranch(named.id)
    }
  }, [hashCode, branches, section])

  // The owner belongs to no organisation; with several, an org-scoped section has nothing to read.
  // Also while the list is still on its way: an org-scoped read without one is refused (422).
  const orgUnchosen = Boolean(orgContext?.needed)
  const shownBranches = organisation ? branches.filter((b) => b.tenantId === organisation) : branches
  const branch = shownBranches.find((b) => b.id === branchId)

  if (available.length === 0 && outside.length === 0) {
    return (
      <div className="app">
        <Topbar title={say('admin.settings')} />
        <main className="page set-page">
          <p className="empty">{say('admin.configuresNothing')}</p>
        </main>
      </div>
    )
  }

  const groups = menuOf(available, outside, owner)
  const entries = groups.flatMap((g) => g.entries.map((entry) => ({ entry, group: g })))

  const firstSection = entries.find((e) => e.entry.kind === 'section')?.entry
  const currentKey: SectionKey | '' = section || (firstSection?.kind === 'section' ? firstSection.key : '')
  const current = currentKey ? available.find((s) => s.key === currentKey) : undefined

  const pick = (key: SectionKey) =>
    guarded(() => {
      setSection(key)
      setHashParts([])
      setFilter('')
      history.replaceState(history.state, '', `${window.location.pathname}#${key}`)
    })
  const openEntry = (e: Entry) => {
    if (e.kind === 'section') pick(e.key)
    else guarded(() => (onNavigate ? onNavigate(e.to) : (window.location.href = e.to)))
  }

  // Only a branch's own page holds edits that a branch switch drops.
  const branchPage = current?.key === 'branches' && hashCode !== '' && hashCode !== NEW
  // One operation profile's own page (#opProfiles/<id>), which draws its own heading.
  const profilePage = current?.key === 'opProfiles' && hashCode !== ''
  // A section of one branch names it first in its address; its records' pages follow it.
  const branchCoded = Boolean(current && BRANCH_CODED.has(current.key))
  const chooseBranch = (id: string) => {
    if (id === branchId) return
    // A record of one branch, open on its page, closes when the branch changes.
    if (branchPage || (branchCoded && hashParts.length > 1)) guarded(() => chosenBranch(id))
    else chosenBranch(id)
  }
  const chosenBranch = (id: string) => {
    setBranchId(id)
    rememberBranch(id)
    // A branch's page follows the top bar; a section of one branch drops the stale code from its address.
    const chosen = branches.find((b) => b.id === id)
    if (branchPage && chosen) {
      passUnsaved()
      window.location.hash = `branches/${encodeURIComponent(chosen.code)}`
    } else if (hashCode && current && branchCoded) {
      setHashParts([])
      history.replaceState(history.state, '', `${window.location.pathname}#${current.key}`)
    }
  }

  // The address below the open section, for its records' pages (settingsKit useSubPage).
  const subKey = current?.key ?? ''
  const subLead = branchCoded && branch ? [branch.code] : []
  const subNav: SubPageNav = {
    here: subKey && hashOf(subKey, subLead),
    path: branchCoded ? hashParts.slice(1) : hashParts,
    open: (...steps) => {
      if (!subKey) return
      // An address with no section shows the first one, which is this one.
      const from = window.location.hash || hashOf(subKey, [])
      history.pushState({ qmsFrom: from }, '', hashOf(subKey, [...subLead, ...steps]))
      closing.current = false
      setHashParts([...subLead, ...steps])
    },
    back: (steps) => {
      if (!subKey || closing.current) return
      const to = hashOf(subKey, [...subLead, ...steps])
      if (to === window.location.hash) return
      // Back when this entry was pushed from there, so the history holds no second list.
      if ((history.state as { qmsFrom?: string } | null)?.qmsFrom === to) {
        closing.current = true
        passUnsaved()
        history.back()
        return
      }
      history.replaceState(null, '', to)
      setHashParts([...subLead, ...steps])
    },
    swap: (...steps) => {
      if (!subKey) return
      // Keeps where the replaced entry came from, so closing the new page still goes Back.
      history.replaceState(history.state, '', hashOf(subKey, [...subLead, ...steps]))
      setHashParts([...subLead, ...steps])
    },
  }

  const leaveAsk = leaving && (
    <DiscardAsk
      label={say('ui.discardAsk')}
      onKeep={() => setLeaving(null)}
      onDiscard={() => {
        const go = leaving
        setLeaving(null)
        go()
      }}
    />
  )

  // The installation owner's own pages stay English; an organisation admin keeps their language.
  const ownerSay = (k: StaffKey) => (current && headingLang(current.group, owner, lang) === 'en' ? t(k, 'en') : say(k))
  const toMenu = (e: Entry): MenuEntry => ({ key: e.key, label: e.label, icon: e.icon, part: e.kind === 'section' ? e.part : undefined, page: e.kind === 'page' })
  const menuGroups: MenuGroup[] = groups.map((g) => ({ key: g.key, label: g.label, entries: g.entries.map(toMenu) }))
  const q = filter.trim().toLocaleLowerCase()
  const found = q
    ? entries
        .filter(({ entry }) => `${say(entry.label)} ${say(entry.also)}`.toLocaleLowerCase().includes(q))
        .map(({ entry, group }) => ({ entry: toMenu(entry), group: menuGroups.find((m) => m.key === group.key)! }))
    : []

  const menu = (
    <SettingsMenu
      groups={menuGroups}
      currentKey={currentKey}
      filter={filter}
      onFilter={setFilter}
      found={found}
      label={say('admin.sections')}
      word={ownerSay}
      onOpen={(m) => {
        const entry = entries.find((x) => x.entry.key === m.key)?.entry
        if (entry) openEntry(entry)
      }}
    />
  )

  if (!current) {
    // Only pages of their own to point at: the menu alone.
    return (
      <div className="app">
        <Topbar title={say('admin.settings')} branch={branch ?? null} branches={shownBranches} onChooseBranch={chooseBranch} />
        <SetupAtNote />
        <main className="page set-page">
          <div className="set-frame">
            {menu}
            <div className="set-main">
              <PageHead title={say('admin.settings')} />
            </div>
          </div>
        </main>
      </div>
    )
  }

  const needsBranch = current.group === 'branch' || current.key === 'bookingBranches'
  const waitsForOrg = orgUnchosen && NEEDS_ORGANISATION.has(current.key)
  const readOnlyQueues = !can('profiles.manage') || (identity.staff.branchIds ?? []).length > 0
  const ownFrame = !LEGACY_FRAME.has(current.key) && !waitsForOrg

  const body = waitsForOrg ? (
    <OrganisationNeeded />
  ) : (
    <>
      {current.key === 'opProfiles' && (
        <OperationProfiles
          key={organisation}
          organisation={organisation || undefined}
          openId={hashCode || undefined}
          onOpen={(id) => {
            window.location.hash = id ? `opProfiles/${id}` : 'opProfiles'
          }}
          readOnly={readOnlyQueues}
        />
      )}
      {current.key === 'services' && <Services organisation={organisation || undefined} branchId={branch?.id} branchName={branch?.name} />}
      {current.key === 'outcomes' && <Outcomes organisation={organisation || undefined} />}
      {current.key === 'marks' && <Marks organisation={organisation || undefined} />}
      {current.key === 'fields' && <Fields organisation={organisation || undefined} />}
      {current.key === 'options' && (
        <Options key={organisation} organisation={organisation || undefined} />
      )}
      {current.key === 'languages' && <Languages key={organisation} organisation={organisation || undefined} />}
      {current.key === 'wording' && <Wording key={organisation} organisation={organisation || undefined} />}
      {current.key === 'equipment' && (
        <EquipmentSection key={organisation} organisation={organisation || undefined} openId={hashCode || undefined} readOnly={readOnlyQueues} />
      )}
      {current.key === 'screenApps' && <ScreenApps organisation={organisation || undefined} canManage={can('tenant.manage')} />}
      {current.key === 'connectors' && <Connectors key={organisation} organisation={organisation || undefined} />}
      {current.key === 'notifications' && <Notifications organisation={organisation || undefined} canConnect={can('tenant.manage')} />}
      {current.key === 'alerts' && branch && <QueueAlerts key={branch.id} branchId={branch.id} />}
      {current.key === 'branches' && (
        <Branches
          key={organisation}
          organisation={organisation || undefined}
          onBranchesChanged={loadBranches}
          links={available.filter((s) => s.group === 'branch').map((s) => ({ key: s.key, label: say(s.label) }))}
        />
      )}
      {current.key === 'network' && (
        <Network
          organisation={organisation || undefined}
          // Groups are shaped only by an administrator not confined to named branches.
          canShape={(identity.staff.branchIds ?? []).length === 0}
        />
      )}
      {current.key === 'roles' && <Roles organisation={organisation || undefined} />}
      {current.key === 'bookingBranches' && branch && <BookingBranches key={branch.id} branch={branch} />}
      {current.key === 'bookingRules' && <BookingRules locked={bookingLocked} />}
      {current.key === 'bookingKeys' && <BookingKeys locked={bookingLocked} />}
      {current.key === 'directory' && (
        // Keyed by the organisation, so switching drops the last one's draft and test result.
        <Directory key={organisation} organisation={organisation || undefined} canConnect={setsUpSignIn} branches={branches} />
      )}
      {current.key === 'sso' && <SingleSignOn key={organisation} organisation={organisation || undefined} canConnect={setsUpSignIn} />}
      {current.key === 'mail' && <Mail key={organisation} organisation={organisation || undefined} />}
      {current.key === 'webhooks' && <Webhooks key={organisation} organisation={organisation || undefined} branches={branches} />}
      {current.key === 'dataFeed' && <DataFeed key={organisation} organisation={organisation || undefined} />}
      {current.key === 'deliveries' && <Deliveries key={organisation} organisation={organisation || undefined} />}
      {current.key === 'licence' && <Licence manage={can('licence.manage')} />}
      {current.key === 'settings' && <Settings />}
      {current.key === 'organisations' && (
        <Organisations
          currentTenantId={identity.staff.tenantId}
          onChanged={() => {
            orgContext?.reload()
            void loadBranches()
          }}
        />
      )}
    </>
  )

  return (
    <div className="app">
      <Topbar title={say('admin.settings')} branch={branch ?? null} branches={shownBranches} onChooseBranch={chooseBranch} />
      <SetupAtNote />
      <main className="page set-page">
        <div className="set-frame">
          {menu}

          <div className="set-main" aria-label={ownerSay(current.label)} role="region">
            {/* A section in the new frame, or a branch or profile page, draws its own head. */}
            {!ownFrame && !branchPage && !profilePage && (
              <PageHead
                title={ownerSay(current.label)}
                line={HINTS[current.key] ? say(HINTS[current.key]!) : undefined}
              />
            )}

            {leaveAsk}
            {error && <Notice>{error}</Notice>}
            {needsBranch && saysNoBranch(branchesRead, branch) && <p className="admin-empty">{say('admin.noBranchYet')}</p>}

            <SubPageProvider value={subNav}>{ownFrame ? body : <Card className="set-legacy">{body}</Card>}</SubPageProvider>
          </div>
        </div>
      </main>
    </div>
  )
}
